/**
 * Contacts surface (live) - one roster card. Clean and read-only: avatar, name,
 * title/company, temperature, signal count, email, and (when present) a source
 * microline. Every action lives in the detail modal. Signal contacts get the
 * teal left-edge accent and a "N signals detected" line. A gold "Follow-up due"
 * cue shows when a stored reminder date is within 7 days or past (8a). Honest
 * absence: a missing email becomes "No email on file"; a missing source line is
 * simply omitted (never placeholder text). Owner chip only in the admin All
 * scope. Hover lift/scale + glow live in contacts.css.
 */

import type { Contact } from "../types";
import {
  TEMPERATURE_LABEL,
  formatDate,
  initials,
  isFollowUpDue,
  isFollowUpOverdue,
  metAtLine,
} from "../derive";
import { IconClock, IconMail, IconPin, IconSignal } from "../contact-icons";

export function ContactCard({
  contact,
  showOwner,
  viewerId,
  onOpen,
}: {
  contact: Contact;
  showOwner: boolean;
  viewerId: string;
  onOpen: (contact: Contact) => void;
}): JSX.Element {
  const signalCount = contact.matchedSignals.length;
  const hasSignals = signalCount > 0;
  const mine = contact.ownerId === viewerId;
  const followUpDue = isFollowUpDue(contact);
  const overdue = isFollowUpOverdue(contact);
  const metAt = metAtLine(contact);

  return (
    <article
      className={`sxc-card${hasSignals ? " sxc-card--signal" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Open ${contact.name || "contact"}`}
      onClick={() => onOpen(contact)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(contact);
        }
      }}
    >
      <div className="sxc-card-top">
        <span className="sxc-avatar" aria-hidden="true">
          {initials(contact.name)}
        </span>
        <div className="sxc-card-id">
          <p className="sxc-card-name">{contact.name || "Unnamed contact"}</p>
          <p className="sxc-card-role">
            {[contact.title, contact.company].filter(Boolean).join(" · ") ||
              "No title or company"}
          </p>
        </div>
        {contact.temperature ? (
          <span className={`sxc-temp sxc-temp--${contact.temperature}`}>
            {TEMPERATURE_LABEL[contact.temperature]}
          </span>
        ) : (
          <span className="sxc-temp sxc-temp--unset" title="No temperature set">
            —
          </span>
        )}
      </div>

      {hasSignals ? (
        <p className="sxc-card-signals">
          <IconSignal />
          {signalCount} signal{signalCount === 1 ? "" : "s"} detected
        </p>
      ) : null}

      <p className="sxc-card-meta">
        {contact.email ? (
          <span className="sxc-meta-item">
            <IconMail />
            {contact.email}
          </span>
        ) : (
          <span className="sxc-meta-item sxc-meta-empty">No email on file</span>
        )}
      </p>

      {metAt ? (
        <p className="sxc-card-metat">
          <IconPin />
          {metAt}
        </p>
      ) : null}

      {followUpDue ? (
        <p className={`sxc-card-followup${overdue ? " sxc-card-followup--overdue" : ""}`}>
          <IconClock />
          {overdue
            ? `Follow-up overdue ${formatDate(contact.followUpDueAt)}`
            : `Follow-up due ${formatDate(contact.followUpDueAt)}`}
        </p>
      ) : null}

      {showOwner ? (
        <div className="sxc-card-foot">
          <span
            className={`sxc-ownchip${mine ? " sxc-ownchip--mine" : " sxc-ownchip--other"}`}
          >
            {mine ? "You" : contact.ownerName}
          </span>
        </div>
      ) : null}
    </article>
  );
}
